import React, { useEffect, useState } from "react";
import { Box, Tag, Text, Tooltip } from "@chakra-ui/react";
import PhoneStatusBadge from "./PhoneStatusBadge";
import { SLOT_LABEL, resolveSmsPhone, smsCandidates } from "../../utils/smsPhone";
import { onNumberStatusChange } from "../../utils/numberStatus";

// A patient's numbers — cell, then home — each with its PhoneStatusBadge, and
// a "texting" tag on the one the cell/home rule (utils/smsPhone) picks. A
// patient with a single number renders exactly as before: number + badge, no
// tag. `compact` / `letter` are passed to the badges; `inline` renders in a
// <span> so it can sit inside a line of text. The resolve goes through the
// same batched, cached lookup the badges use, so it costs no extra request.
export default function PatientPhones({
  row,
  compact = false,
  letter = false,
  inline = false,
  prefix = "",
  separator = " · ",
  fontSize = "sm",
  color,
  ...boxProps
}) {
  const candidates = smsCandidates(row);
  const several = candidates.length > 1;
  const [pick, setPick] = useState(null);
  const key = candidates.map((c) => c.phone).join("|");

  useEffect(() => {
    if (!several) {
      setPick(null);
      return undefined;
    }
    let live = true;
    const read = () =>
      resolveSmsPhone(row).then((res) => {
        if (live) setPick(res);
      });
    read();
    const unsubscribe = onNumberStatusChange(read);
    return () => {
      live = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, several]);

  if (!candidates.length) return null;

  const tagFor = (c) => {
    if (!several || !pick) return null;
    if (pick.blocked) {
      if (c.slot !== "cell") return null;
      const optOut = pick.reason === "opt_out";
      return (
        <Tag size="sm" colorScheme={optOut ? "purple" : "red"} ml={1} verticalAlign="middle" data-testid="sms-no-text">
          {optOut ? "Opted out — no texts" : "Both numbers bad — no texts"}
        </Tag>
      );
    }
    if (c.slot !== pick.slot) return null;
    const why = c.slot === "home" ? " — the cell is a bad number" : "";
    return (
      <Tooltip label={`Texts go to the ${SLOT_LABEL[c.slot].toLowerCase()} phone${why}`} hasArrow>
        <Tag size="sm" colorScheme="blue" ml={1} verticalAlign="middle" cursor="help" data-testid="sms-texting">
          texting
        </Tag>
      </Tooltip>
    );
  };

  const items = candidates.map((c, i) => (
    <Box as="span" key={c.slot} whiteSpace="nowrap">
      {i > 0 ? separator : prefix}
      {several && (
        <Text as="span" color="gray.500" fontSize="xs" mr={1}>
          {SLOT_LABEL[c.slot]}
        </Text>
      )}
      {c.phone}
      <PhoneStatusBadge phone={c.phone} compact={compact} letter={letter} ml={1} />
      {tagFor(c)}
    </Box>
  ));

  if (inline) {
    return (
      <Box as="span" {...boxProps}>
        {items}
      </Box>
    );
  }
  return (
    <Text fontSize={fontSize} color={color} {...boxProps}>
      {items}
    </Text>
  );
}
